import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSDatePicker,
} from '../../../shared/components/form-controls';
import SearchPlaceAutoComplete from '../../../shared/components/modules/SearchPlaceAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import SectionLabel from './SectionLabel';
import PartyKindLookup from './PartyKindLookup';
import DictionaryManagementDialog from './DictionaryManagementDialog';
import AddPlaceDialog from './AddPlaceDialog';
import addVehicleSchema from '../../schema/addVehicleSchema';
import {
  ADD_VEHICLE_DEFAULT_VALUES,
  DICT_SELECT_PROPS,
} from '../../constants/addVehicleFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createVehicle, searchPlaceAddresses } from '../../services/userService';

const AddVehicleDialog = ({ open, onClose, onVehicleCreated }) => {
  const dispatch = useDispatch();
  const makeDictPerms = useSelector((state) => state.permissions?.dictionary?.codeVehicleMakes);
  const styleDictPerms = useSelector((state) => state.permissions?.dictionary?.codeVehicleStyles);
  const [submitting, setSubmitting] = useState(false);
  const [authorityAddressOptions, setAuthorityAddressOptions] = useState([]);
  const [, setLoadingAuthorityAddress] = useState(false);
  const [makeManagementOpen, setMakeManagementOpen] = useState(false);
  const [styleManagementOpen, setStyleManagementOpen] = useState(false);
  const [addPlaceDialogOpen, setAddPlaceDialogOpen] = useState(false);

  const methods = useForm({
    resolver: CreateFormResolver(addVehicleSchema),
    mode: 'onSubmit',
    defaultValues: ADD_VEHICLE_DEFAULT_VALUES,
  });

  const { control, setValue } = methods;

  const watched = useWatch({
    control,
    name: [
      'ptsPlateIssuingAuthorityID',
      'Make',
      'GarageIndicator',
      'ptsParentID',
      'ptsLesseeID',
      'ptsLessorID',
    ],
  });
  const [
    ptsPlateIssuingAuthorityID,
    Make,
    GarageIndicator,
    ptsParentID,
    ptsLesseeID,
    ptsLessorID,
  ] = watched;

  const canSaveVehicle = true;
  const showMakeAddIcon = Boolean(
    makeDictPerms?.Create || makeDictPerms?.Edit || makeDictPerms?.Delete
  );
  const showStyleAddIcon = Boolean(
    styleDictPerms?.Create || styleDictPerms?.Edit || styleDictPerms?.Delete
  );
  const { isDirty } = useFormState({ control });
  const {
    stateOptions,
    plateTypeOptions,
    plateColorOptions,
    plateStatusOptions,
    plateDetailOptions,
    vehicleMakeOptions,
    vehicleMakesModelsOptions,
    vehicleStyleOptions,
    vehicleColorOptions,
    vehicleClassificationOptions,
    vehicleBrandOptions,
    vehicleBranderOptions,
    vehiclePropertyOptions,
    vehicleCommercialOptions,
    transmissionOptions,
    garageFacilityOptions,
  } = useUserDictionaryOptions();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const modelOptions = useMemo(() => {
    if (!Make) return [];
    return vehicleMakesModelsOptions
      .filter((mm) => mm.MakeCode === Make)
      .map((mm) => ({ Code: mm.ModelCode, Description: mm.ModelDescription }));
  }, [Make, vehicleMakesModelsOptions]);

  const handleMakeChange = useCallback(() => setValue('Model', ''), [setValue]);

  const handlePlaceCreated = useCallback(
    (ptsPlaceID) => {
      setValue('ptsPlateIssuingAuthorityID', ptsPlaceID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPlaceDialogOpen(false);
    },
    [setValue]
  );

  const onSubmit = useCallback(
    async (data) => {
      const vehicleData = {
        Year: data.Year || null,
        Make: data.Make || null,
        Model: data.Model || null,
        Style: data.Style || null,
        DoorQuantity: data.DoorQuantity || null,
        PassengerSafeQty: data.PassengerSafeQty || null,
        SeatQty: data.SeatQty || null,
        PrimaryColor: data.PrimaryColor || null,
        SecondaryColor: data.SecondaryColor || null,
        InteriorColor: data.InteriorColor || null,
        Classification: data.Classification || null,
        Description: data.Description || null,
        Brand: data.Brand || null,
        Brander: data.Brander || null,
        PropertyID: data.PropertyID || null,
        VIN: data.VIN || null,
        VINAnalysisText: data.VINAnalysisText || null,
        Commercial: data.Commercial || null,
        CommercialID: data.CommercialID || null,
        Property: data.Property || null,
        EmissionInspectionQty: data.EmissionInspectionQty || null,
        Transmission: data.Transmission || null,
        IsCMVIndicator: data.IsCMVIndicator || false,
        IsRented: data.IsRented || false,
        IsWanted: data.IsWanted || false,
        GarageFacility: data.GarageFacility || null,
        GarageIndicator: data.GarageIndicator || false,
        IsTowed: false,
        IsDeleted: false,
      };

      const vehRegData = {
        PlateNumber: data.PlateNumber,
        PlateState: data.PlateState,
        PlateType: data.PlateType || null,
        PlateExpiration: data.PlateExpiration || null,
        ptsPlateIssuingAuthorityID: data.ptsPlateIssuingAuthorityID || null,
        ptsAddressID: data.ptsAddressID || null,
        PlateColor: data.PlateColor || null,
        PlateBackgroundText: data.PlateBackgroundText || null,
        PlateDetail: data.PlateDetail || null,
        PlateStatus: data.PlateStatus || null,
        DecalExpiration: data.DecalExpiration || null,
        InsuranceCompany: data.InsuranceCompany || null,
        OdometerReading: data.OdometerReading || null,
        TitleText: data.TitleText || null,
        VehicleValue: data.VehicleValue || null,
        GrossWeightMeasure: data.GrossWeightMeasure || null,
        IsLeased: data.IsLeased || false,
        IsPermanentReg: data.IsPermanentReg || false,
        RoadIndicator: data.RoadIndicator || false,
        PartyType: data.PartyType || null,
        ptsParentID: data.ptsParentID || null,
        LesseeType: data.LesseeType || null,
        LessorType: data.LessorType || null,
        ptsLesseeID: data.ptsLesseeID || null,
        ptsLessorID: data.ptsLessorID || null,
        IsDeleted: false,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => createVehicle({ vehicleData, vehRegData }),
        onSuccess: (response) => {
          dispatch(notify('Vehicle created successfully', 'success'));
          onVehicleCreated?.(response);
        },
      });
    },
    [dispatch, onVehicleCreated]
  );

  const handleSave = useMemo(() => methods.handleSubmit(onSubmit), [methods, onSubmit]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this vehicle. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }
    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    if (!ptsPlateIssuingAuthorityID) {
      setAuthorityAddressOptions([]);
      return;
    }

    executeApiAction({
      setLoading: setLoadingAuthorityAddress,
      dispatch,
      action: () => searchPlaceAddresses(ptsPlateIssuingAuthorityID),
      onSuccess: (addresses) => setAuthorityAddressOptions(addresses),
    });
  }, [ptsPlateIssuingAuthorityID, dispatch]);

  return (
    <RMSCommonDialog
      open={open}
      title="Add Vehicle"
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting || !canSaveVehicle}
      maxWidth="lg"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}>
      <FormProvider {...methods}>
        <Box className="rms-dialog-section">
          <SectionLabel>Party</SectionLabel>
          <Grid container spacing={2}>
            <PartyKindLookup
              typeFieldName="PartyType"
              idFieldName="ptsParentID"
              typeLabel="Type"
              idLabel="Party Search"
              idValue={ptsParentID}
            />
          </Grid>
        </Box>

        <Box className="rms-dialog-section">
          <SectionLabel>Plate</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <RMSTextField
                name="PlateNumber"
                label="Plate Number"
                inputProps={{ maxLength: 25 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PlateState"
                label="Plate State"
                options={stateOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PlateType"
                label="Plate Type"
                options={plateTypeOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSDatePicker name="PlateExpiration" label="Plate Expiration Date" />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PlateColor"
                label="Plate Color"
                options={plateColorOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PlateStatus"
                label="Plate Status"
                options={plateStatusOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PlateDetail"
                label="Plate Detail"
                options={plateDetailOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSDatePicker name="DecalExpiration" label="Decal Expiration" />
            </Grid>
            <Grid item xs={8}>
              <RMSTextField
                name="PlateBackgroundText"
                label="Plate Background Text"
                inputProps={{ maxLength: 2000 }}
              />
            </Grid>
            <Grid item xs={6}>
              <SearchPlaceAutoComplete
                name="ptsPlateIssuingAuthorityID"
                label="Plate Issuing Authority"
                defaultPlaceId={ptsPlateIssuingAuthorityID}
                onAddIconClick={() => setAddPlaceDialogOpen(true)}
                showDetailsIcon={false}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="ptsAddressID"
                label="Issuing Authority Address"
                options={authorityAddressOptions}
                optionValue="ptsAddressID"
                optionLabel="FullAddressText"
                showLabelOnly
                disabled={!ptsPlateIssuingAuthorityID}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="rms-dialog-section">
          <SectionLabel>Details</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <RMSTextField
                name="Year"
                label="Year"
                type="number"
                inputProps={{ min: 0, max: 2100 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="Make"
                label="Make"
                options={vehicleMakeOptions}
                onChange={handleMakeChange}
                prefixIcon={showMakeAddIcon ? <AddIcon fontSize="small" /> : undefined}
                onPrefixIconClick={showMakeAddIcon ? () => setMakeManagementOpen(true) : undefined}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect name="Model" label="Model" options={modelOptions} {...DICT_SELECT_PROPS} />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="Style"
                label="Style"
                options={vehicleStyleOptions}
                prefixIcon={showStyleAddIcon ? <AddIcon fontSize="small" /> : undefined}
                onPrefixIconClick={
                  showStyleAddIcon ? () => setStyleManagementOpen(true) : undefined
                }
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="DoorQuantity"
                label="Doors"
                type="number"
                inputProps={{ min: 0, max: 20 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="PassengerSafeQty"
                label="Passenger Quantity"
                type="number"
                inputProps={{ min: 0, max: 1000 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="SeatQty"
                label="Seat Quantity"
                type="number"
                inputProps={{ min: 0, max: 1000 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="Classification"
                label="Classification"
                options={vehicleClassificationOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="PrimaryColor"
                label="Primary Color"
                options={vehicleColorOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="SecondaryColor"
                label="Secondary Color"
                options={vehicleColorOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="InteriorColor"
                label="Interior Color"
                options={vehicleColorOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Brand"
                label="Brand"
                options={vehicleBrandOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Brander"
                label="Brander"
                options={vehicleBranderOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <RMSTextField
                name="Description"
                label="Description"
                multiline
                rows={2}
                inputProps={{ maxLength: 8000 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="rms-dialog-section">
          <SectionLabel>Credentials and Analysis</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <RMSTextField name="VIN" label="VIN" inputProps={{ maxLength: 17 }} />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="VINAnalysisText"
                label="VIN Analysis Text"
                inputProps={{ maxLength: 2000 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="Transmission"
                label="Transmission Code"
                options={transmissionOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField name="PropertyID" label="Property ID" inputProps={{ maxLength: 25 }} />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="Property"
                label="Property Code"
                options={vehiclePropertyOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="EmissionInspectionQty"
                label="Emission Inspection"
                type="number"
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="CommercialID"
                label="Commercial ID"
                inputProps={{ maxLength: 25 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="Commercial"
                label="Commercial Code"
                options={vehicleCommercialOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSCheckbox name="IsCMVIndicator" label="CMV" />
            </Grid>
          </Grid>
        </Box>

        <Box className="rms-dialog-section">
          <SectionLabel>Flags</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <RMSCheckbox name="IsRented" label="Rented" />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="IsWanted" label="Wanted" />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="GarageIndicator" label="Garage Indicator" />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="GarageFacility"
                label="Garage Facility"
                options={garageFacilityOptions}
                disabled={!GarageIndicator}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="rms-dialog-section rms-dialog-section--last">
          <SectionLabel>Registration</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <RMSTextField name="TitleText" label="Title Text" inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="InsuranceCompany"
                label="Insurance Company"
                inputProps={{ maxLength: 300 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="VehicleValue"
                label="Vehicle Value"
                type="number"
                inputProps={{ min: 0, max: 100000000000 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="OdometerReading"
                label="Odometer Reading"
                type="number"
                inputProps={{ min: -900000000, max: 900000000 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="GrossWeightMeasure"
                label="Gross Weight Measure"
                type="number"
                inputProps={{ min: -90, max: 90 }}
              />
            </Grid>
            <Grid item xs={4}>
              <RMSCheckbox name="IsPermanentReg" label="Permanent Registration" />
            </Grid>
            <Grid item xs={4}>
              <RMSCheckbox name="RoadIndicator" label="Road Indicator" />
            </Grid>
            <Grid item xs={4}>
              <RMSCheckbox name="IsLeased" label="Leased" />
            </Grid>

            <PartyKindLookup
              typeFieldName="LesseeType"
              idFieldName="ptsLesseeID"
              typeLabel="Lessee"
              idLabel="Lessee Search"
              idValue={ptsLesseeID}
            />
            <PartyKindLookup
              typeFieldName="LessorType"
              idFieldName="ptsLessorID"
              typeLabel="Lessor"
              idLabel="Lessor Search"
              idValue={ptsLessorID}
            />
          </Grid>
        </Box>
      </FormProvider>

      {makeManagementOpen && (
        <DictionaryManagementDialog
          open={makeManagementOpen}
          dictionary="codeVehicleMakes"
          title="Make"
          onClose={() => setMakeManagementOpen(false)}
        />
      )}

      {styleManagementOpen && (
        <DictionaryManagementDialog
          open={styleManagementOpen}
          dictionary="codeVehicleStyles"
          title="Style"
          onClose={() => setStyleManagementOpen(false)}
        />
      )}

      {addPlaceDialogOpen && (
        <AddPlaceDialog
          open={addPlaceDialogOpen}
          onClose={() => setAddPlaceDialogOpen(false)}
          onPlaceCreated={handlePlaceCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default AddVehicleDialog;
